import { useEffect, useState } from 'react';

export type AsyncContentfulQueryResult<T> = {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
};

/**
 * Runs a one-shot async load when dependencies change; ignores stale responses after unmount or re-run.
 */
export function useAsyncContentfulQuery<T>(
  load: () => Promise<T>,
  deps: readonly unknown[]
): AsyncContentfulQueryResult<T> {
  const [data, setData] = useState<T | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    load()
      .then((result) => {
        if (!cancelled) {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller supplies complete dependency list
  }, deps);

  return { data, isLoading, isError };
}
